import { useState, useCallback, useRef, useMemo } from 'react';

import { Heading, Flex, Button, Text, SimpleGrid } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getStudyDataChartView } from 'services/studies';
import { parseRunStudyConfigDate } from 'services/items';
import { merge } from 'lodash';

import SimpleGridSkeleton from '../utils/SimpleGridSkeleton';

import ComboboxField from '../forms/ComboboxField';

import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import psVHighchartsTheme from '../../theme/highcharts-theme';

const ReviewForwardData = ({
    forwardCurves,
    formRef,
    otherProperties,
    runSchedule,
    curvePriceTypeId,
    curveSourceId,
    assignedIndex = -1,
    selectedIndex = 0,
    portfolios = [],
    ...rest
}) => {
    const intl = useIntl();
    const chartInstance = useRef(null);

    const [hasChartData, setHasChartData] = useState(false);
    const [selectedForwardCurve, setSelectedForwardCurve] = useState(null);
    const [reviewButtonClicked, setReviewButtonClicked] = useState(false);
    const [loadingForwardData, setLoadingForwardData] = useState(true);
    const [options, setOptions] = useState({});

    const hasForwardCurves = forwardCurves.length > 0;
    const isStudyReady = formRef?.current?.values?.simulationStart && formRef?.current?.values?.simulationEnd;

    const apiPayload = useMemo(() => {
        if (isStudyReady) {
            let maxCDTransactionDate, maxCDCreateDate, validationBusinessDays;
            if (otherProperties) {
                const lines = otherProperties.split('\n');
                for (const line of lines) {
                    const [key, value] = line.split('=');
                    if (key && value) {
                        const keyLower = key.trim().toLowerCase();
                        if (keyLower === 'maxcdtransactiondate') {
                            maxCDTransactionDate = parseRunStudyConfigDate(value.trim());
                        } else if (keyLower === 'maxcdcreatedate') {
                            maxCDCreateDate = parseRunStudyConfigDate(value.trim());
                        } else if (keyLower === 'validationbusinessdays') {
                            validationBusinessDays = parseInt(value.trim(), 10);
                        }
                    }
                }
            }

            // Set default values if not found in otherProperties
            maxCDTransactionDate = maxCDTransactionDate || runSchedule;
            maxCDCreateDate = maxCDCreateDate || runSchedule;
            validationBusinessDays = validationBusinessDays || 252; // 252 is approximately one year of trading days

            const payload = {
                studyStartDate: formRef?.current?.values?.simulationStart,
                studyEndDate: formRef?.current?.values?.simulationEnd,
                scheduledStudyRunDate: runSchedule,
                priceTypeId: curvePriceTypeId,
                curveSourceId: curveSourceId,
                maxCDTransactionDate: maxCDTransactionDate,
                maxCDCreateDate: maxCDCreateDate,
                validationBusinessDays: validationBusinessDays,
            };
            return payload;
        } else {
            return {};
        }
    }, [formRef, otherProperties, runSchedule, curvePriceTypeId, curveSourceId, isStudyReady]);

    const onReviewClicked = useCallback(async () => {
        setLoadingForwardData(true);
        setReviewButtonClicked(true);

        await getStudyDataChartView(selectedForwardCurve, apiPayload)
            .then((res) => {
                // The highcharts-theme.js file handles most styling needs
                const customOptions = {
                    ...res.options,
                    // Add any chart-specific options that aren't in the theme
                    chart: {
                        ...res.options.chart,
                    },
                    exporting: {
                        enabled: true,
                        tableCaption: 'Data Table for Forward Curve',
                        // Function to apply styling to the data table after it's created
                        buttons: {
                            contextButton: {
                                menuItems: ['viewFullscreen', 'printChart', 'separator', 'viewData'],
                                symbol: 'menu',
                            },
                        },
                    },
                    // Custom axis settings for this specific chart
                    xAxis: {
                        ...(res.options.xAxis || {}),
                        title: {
                            margin: 10,
                            ...(res.options.xAxis?.title || {}),
                        },
                    },
                    yAxis: {
                        ...(res.options.yAxis || {}),
                        labels: {
                            x: -8,
                            y: 3,
                            formatter: function () {
                                return '$' + this.value;
                            },
                            ...(res.options.yAxis?.labels || {}),
                        },
                        title: {
                            offset: 55, // Add offset to the Y-axis title
                            text: 'Price ($)', // Add unit to the Y-axis title
                            ...(res.options.yAxis?.title || {}),
                        },
                    },
                };

                // Check if there's valid series data to display
                let hasData = false;
                if (!res.options.series || !Array.isArray(res.options.series) || res.options.series.length === 0) {
                    // No series data found
                    setHasChartData(hasData);
                } else {
                    // Check if at least one series has data
                    hasData = res.options.series.some(
                        (series) => series.data && Array.isArray(series.data) && series.data.length > 0
                    );
                    setHasChartData(hasData);
                }

                setLoadingForwardData(false);

                if (!hasData) {
                    //clear chart options
                    setOptions({});
                } else {
                    // Merge theme with customOptions, with customOptions taking precedence
                    const mergedOptions = merge({}, psVHighchartsTheme, customOptions);
                    setOptions(mergedOptions);
                }
            })
            .catch((error) => {
                setReviewButtonClicked(false);
                setLoadingForwardData(false);
            });
    }, [selectedForwardCurve, setLoadingForwardData, setReviewButtonClicked, apiPayload, setOptions]);

    const changeForwardCurve = useCallback(
        (value) => {
            setSelectedForwardCurve(value);
        },
        [setSelectedForwardCurve]
    );

    return (
        <>
            <Heading as="h3" variant="h3">
                <FormattedMessage id="run_a_study_data_forward_curve_heading" />
            </Heading>
            {hasForwardCurves && isStudyReady && (
                <>
                    <SimpleGrid columns={{ xl: 2 }} spacingX={6} spacingY={4} mt={4} mb={4}>
                        <ComboboxField
                            mb={3}
                            id="forwardCurveToReview"
                            name="forwardCurveToReview"
                            label={intl.formatMessage({ id: 'run_a_study_data_select_forward_curve' })}
                            options={forwardCurves}
                            valueKey="id"
                            labelKey="name"
                            showPlaceholder={false}
                            placeholderLabel={intl.formatMessage({
                                id: 'run_a_study_data_select_forward_curve',
                            })}
                            onChange={changeForwardCurve}
                            isControlled={false}
                        />
                        <Flex justifyContent="flex-start">
                            <Button
                                mt={6}
                                ml={5}
                                variant="primary"
                                textTransform="capitalize"
                                isDisabled={!selectedForwardCurve}
                                onClick={onReviewClicked}
                                isLoading={loadingForwardData && reviewButtonClicked}
                            >
                                <FormattedMessage id="common_review" />
                            </Button>
                        </Flex>
                    </SimpleGrid>
                    {loadingForwardData && reviewButtonClicked && (
                        <>
                            <SimpleGridSkeleton columns={{ xl: 3 }} spacingX={6} spacingY={4} mb={4} />
                        </>
                    )}
                    {!loadingForwardData &&
                        reviewButtonClicked &&
                        (hasChartData ? (
                            <>
                                <HighchartsReact
                                    ref={chartInstance}
                                    highcharts={Highcharts}
                                    constructorType={'stockChart'}
                                    options={options}
                                    containerProps={{ style: { width: '100%', height: '450px' } }}
                                />
                            </>
                        ) : (
                            <Text fontWeight={400} color="black" _dark={{ color: 'black' }} mt={4}>
                                <FormattedMessage id="run_a_study_data_no_forward_series" />
                            </Text>
                        ))}
                </>
            )}
            {!hasForwardCurves && (
                <>
                    <Text fontWeight={400} color="black" _dark={{ color: 'black' }} mt={4}>
                        <FormattedMessage id="run_a_study_data_no_forward_curves" />
                    </Text>
                </>
            )}
            {!isStudyReady && hasForwardCurves && (
                <>
                    <Text fontWeight={400} color="black" _dark={{ color: 'black' }} mt={4}>
                        <FormattedMessage id="run_a_study_data_study_not_configured_forward_curves" />
                    </Text>
                </>
            )}
        </>
    );
};

export default ReviewForwardData;
